import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { LLMProviderFamily, ProviderConfig, OperationConfig } from '../../types/settings';
import type { ReasoningEffort, ReasoningSummaryMode } from '@padcanvas/shared';
import { getProviderCapabilities, isOpenAIReasoningModel } from '@padcanvas/shared';
import { DataTable, type ColumnDef, type SortState, Modal, Input, NumberInput, Select, SearchFilterBar, type FilterDef, Button } from '@shared-ui/components';
import './Providers.css';

const FAMILY_OPTIONS: { value: LLMProviderFamily; label: string }[] = [
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'google', label: 'Google' },
  { value: 'xai', label: 'xAI (Grok)' },
  { value: 'deepseek', label: 'DeepSeek' },
];

// Filters for the SearchFilterBar (family/status AND with the free-text search).
const PROVIDER_FILTERS: FilterDef[] = [
  { key: 'family', placeholder: 'All families', options: [...FAMILY_OPTIONS, { value: 'mock', label: 'Mock' }] },
  { key: 'status', placeholder: 'All statuses', options: [{ value: 'active', label: 'Active' }, { value: 'deprecated', label: 'Deprecated' }] },
];

// Keys from the original seed definitions that cannot be deleted
const BUILT_IN_PROVIDER_KEYS = [
  'gpt-4o',
  'gpt-4.1',
  'gpt-5',
  'gpt-5-mini',
  'gpt-4o-realtime',
  'claude-haiku-4-5',
  'claude-sonnet-4-5',
  'claude-opus-4-6',
  'gemini-2.5-flash-lite',
  'gemini-2.5-flash',
  'gemini-2.5-pro',
];

type OpKey = 'analysis' | 'hint' | 'textchat' | 'realtimeVoice';
type ModalMode = 'add' | 'edit' | null;

/** OpenAI Realtime API voices. Hardcoded — these are fixed by OpenAI's API,
 *  not derived from the backend. Update when OpenAI ships new voices. */
const OPENAI_REALTIME_VOICES = [
  'alloy',
  'ash',
  'ballad',
  'coral',
  'echo',
  'sage',
  'shimmer',
  'verse',
];

/** Inline style for form inputs/selects in the modal. */
const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  height: 34,
  padding: '6px 10px',
  border: '1px solid #4b5563',
  background: '#111827',
  color: '#e5e7eb',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
};

/**
 * Strip capability-irrelevant fields from the form before saving.
 * Only fields supported by getProviderCapabilities() are persisted.
 */
function stripIrrelevantFields(provider: ProviderConfig): ProviderConfig {
  const caps = getProviderCapabilities(provider.family, provider.modelName);
  const cleaned = { ...provider };
  if (!caps.temperature) delete cleaned.temperature;
  if (!caps.maxTokens) delete cleaned.maxTokens;
  if (!caps.reasoningEffort) delete cleaned.reasoningEffort;
  if (!caps.reasoningSummary) delete cleaned.reasoningSummary;
  if (!caps.thinkingBudgetTokens) delete cleaned.thinkingBudgetTokens;
  // apiKeyEnv is honored by the google family only (callProvider.invokeAdapter).
  if (provider.family !== 'google') delete cleaned.apiKeyEnv;
  return cleaned;
}

export function Providers({ initialSearch }: { initialSearch?: string } = {}) {
  const queryClient = useQueryClient();

  const [providerConfigs, setProviderConfigs] = useState<ProviderConfig[]>([]);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<ProviderConfig>>({});
  const [providerSaveStatus, setProviderSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [sort, setSort] = useState<SortState | undefined>();
  // Search + family/status filters, owned by the SearchFilterBar (search lives
  // under the built-in `search` key). All combine (AND) in `visibleProviders`.
  // Seeded when you arrive by clicking a provider on the Pipelines page — the
  // list filters straight down to the one you asked about, and the search box
  // shows why, so it is obvious how to clear it.
  const [filters, setFilters] = useState<Record<string, string>>(
    initialSearch ? { search: initialSearch } : {},
  );
  const search = filters.search || '';
  const familyFilter = filters.family || '';
  const statusFilter = filters.status || '';

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: adminApi.getSettings,
  });

  // Env-var NAMES set on the function app, keyed by family — drives the
  // apiKeyEnv dropdown so only existing keys can be picked.
  const { data: apiKeyEnvs } = useQuery({
    queryKey: ['apiKeyEnvs'],
    queryFn: adminApi.getApiKeyEnvs,
    staleTime: 5 * 60_000,
  });

  // Vendor model ids for the selected family — drives the Model Name
  // dropdown. Fetched only while the modal is open; empty list (key missing
  // or vendor call failed) falls back to free-text entry.
  const [modelNameCustom, setModelNameCustom] = useState(false);
  const { data: familyModels } = useQuery({
    queryKey: ['providerModels', form.family],
    queryFn: () => adminApi.getProviderModels(form.family!),
    enabled: modalMode !== null && !!form.family && form.family !== 'mock',
    staleTime: 5 * 60_000,
  });

  const { data: providerConfigData, isLoading: providerConfigLoading } = useQuery({
    queryKey: ['providerConfig'],
    queryFn: adminApi.getProviderConfig,
  });

  useEffect(() => {
    if (providerConfigData?.providers) {
      setProviderConfigs(providerConfigData.providers);
    }
  }, [providerConfigData]);

  const providerConfigMutation = useMutation({
    mutationFn: (providers: ProviderConfig[]) => adminApi.updateProviderConfig(providers),
    onMutate: () => setProviderSaveStatus('saving'),
    onSuccess: (result) => {
      setProviderConfigs(result.providers);
      setProviderSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['providerConfig'] });
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setTimeout(() => setProviderSaveStatus('idle'), 2000);
    },
    onError: () => {
      setProviderSaveStatus('error');
      setTimeout(() => setProviderSaveStatus('idle'), 3000);
    },
  });

  const settingsMutation = useMutation({
    mutationFn: (newSettings: Partial<import('../../types/settings').AppSettings>) => adminApi.updateSettings(newSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  // Remove a provider from all step runs if it was disabled
  function removeProviderFromOperations(
    ops: Record<OpKey, OperationConfig | undefined>,
    removedProvider: string,
    newEnabled: string[]
  ): Record<OpKey, OperationConfig | undefined> {
    if (newEnabled.includes(removedProvider)) return ops;

    const cleanOp = (op: OperationConfig | undefined): OperationConfig | undefined => {
      if (!op) return undefined;
      return {
        pipelines: op.pipelines.map(p => ({
          ...p,
          steps: p.steps.map(s => ({
            ...s,
            runs: s.runs.filter(r => r.provider !== removedProvider),
          })),
        })),
      };
    };

    return {
      analysis: cleanOp(ops.analysis),
      hint: cleanOp(ops.hint),
      textchat: cleanOp(ops.textchat),
      realtimeVoice: cleanOp(ops.realtimeVoice),
    };
  }

  /** Flip a provider between active and deprecated — the only usability axis.
   *  Deprecating has teeth: `LLMProvider.fromKey` throws for deprecated keys, so
   *  the provider stops being callable the moment this saves. */
  const handleProviderToggle = (providerKey: string) => {
    const target = providerConfigs.find(p => p.key === providerKey);
    if (!target) return;
    const nextStatus: ProviderConfig['status'] = target.status === 'deprecated' ? 'active' : 'deprecated';
    const updated = providerConfigs.map(p =>
      p.key === providerKey ? { ...p, status: nextStatus } : p
    );
    if (updated.filter(p => p.status !== 'deprecated').length === 0) return;

    setProviderConfigs(updated);
    providerConfigMutation.mutate(updated);

    // A deprecated provider left in the legacy operations map would throw at
    // call time, so strip it out rather than leave a reference that now fails.
    if (nextStatus === 'deprecated') {
      const activeKeys = updated.filter(p => p.status !== 'deprecated').map(p => p.key) as string[];
      const ops = { ...(settings?.operations || {}) } as Record<OpKey, OperationConfig | undefined>;
      const cleaned = removeProviderFromOperations(ops, providerKey, activeKeys);
      settingsMutation.mutate({ operations: cleaned });
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setEditingKey(null);
    setModelNameCustom(false);
    setForm({ family: 'openai', pricing: { input: 0, output: 0 } });
  };

  const openEditModal = (provider: ProviderConfig) => {
    setModalMode('edit');
    setEditingKey(provider.key);
    setModelNameCustom(false);
    setForm({ ...provider });
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingKey(null);
    setModelNameCustom(false);
    setForm({});
  };

  const handleSave = () => {
    if (!form.key || !form.displayName || !form.modelName || !form.family) return;

    if (modalMode === 'add') {
      if (providerConfigs.some(p => p.key === form.key)) return;
      const newProvider = stripIrrelevantFields({
        key: form.key!,
        displayName: form.displayName!,
        family: form.family as LLMProviderFamily,
        modelName: form.modelName!,
        pricing: form.pricing || { input: 0, output: 0 },
        status: form.status || 'active',
        ...(form.backupProvider ? { backupProvider: form.backupProvider } : {}),
        ...(form.apiKeyEnv ? { apiKeyEnv: form.apiKeyEnv } : {}),
        // LLM defaults
        temperature: form.temperature,
        maxTokens: form.maxTokens,
        reasoningEffort: form.reasoningEffort,
        reasoningSummary: form.reasoningSummary,
        thinkingBudgetTokens: form.thinkingBudgetTokens,
        // Realtime / voice
        voice: form.voice,
        url: form.url,
      });
      const updated = [...providerConfigs, newProvider];
      setProviderConfigs(updated);
      providerConfigMutation.mutate(updated);
    } else if (modalMode === 'edit' && editingKey) {
      const updated = providerConfigs.map(p =>
        p.key === editingKey ? stripIrrelevantFields({ ...p, ...form } as ProviderConfig) : p
      );
      setProviderConfigs(updated);
      providerConfigMutation.mutate(updated);
    }
    closeModal();
  };

  const handleProviderDelete = (key: string) => {
    if (BUILT_IN_PROVIDER_KEYS.includes(key)) return;
    if (!confirm(`Delete provider "${key}"?`)) return;
    const updated = providerConfigs.filter(p => p.key !== key);
    setProviderConfigs(updated);
    providerConfigMutation.mutate(updated);
  };

  // ── Capability-driven adaptive fields ──────────────────────────────
  const caps = getProviderCapabilities(
    (form.family || 'openai') as LLMProviderFamily,
    form.modelName || '',
  );
  const isReasoning = form.family === 'openai' && isOpenAIReasoningModel(form.modelName || '');

  const columns: ColumnDef<ProviderConfig>[] = useMemo(() => [
    {
      key: 'active',
      header: 'Active',
      sortable: true,
      sortValue: (p) => (p.status !== 'deprecated' ? 0 : 1),
      render: (p) => (
        <input
          type="checkbox"
          className="provider-active-check"
          checked={p.status !== 'deprecated'}
          onChange={() => handleProviderToggle(p.key)}
          disabled={providerConfigMutation.isPending}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      key: 'key',
      header: 'Key',
      sortable: true,
      sortValue: (p) => p.key.toLowerCase(),
      render: (p) => <span className="provider-key">{p.key}</span>,
    },
    {
      key: 'displayName',
      header: 'Display Name',
      sortable: true,
      sortValue: (p) => p.displayName.toLowerCase(),
      render: (p) => p.displayName,
    },
    {
      key: 'family',
      header: 'Family',
      sortable: true,
      sortValue: (p) => p.family,
      render: (p) => <span className={`family-badge family-${p.family}`}>{p.family}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortValue: (p) => p.status || 'active',
      render: (p) => {
        const status = p.status || 'active';
        return (
          <span
            style={{
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 600,
              backgroundColor: status === 'deprecated' ? '#fee2e2' : '#dcfce7',
              color: status === 'deprecated' ? '#991b1b' : '#166534',
              textTransform: 'uppercase',
            }}
          >
            {status}
          </span>
        );
      },
    },
    {
      key: 'modelName',
      header: 'Model',
      sortable: true,
      sortValue: (p) => p.modelName.toLowerCase(),
      render: (p) => <code className="model-name">{p.modelName}</code>,
    },
    {
      key: 'backup',
      header: 'Backup',
      sortable: true,
      // Providers with no backup sort last ascending — the named ones are what
      // you're scanning for when you sort this column.
      sortValue: (p) => p.backupProvider ?? '￿',
      render: (p) => p.backupProvider
        ? <code style={{ fontSize: 11, color: '#a78bfa' }}>{p.backupProvider}</code>
        : <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: 11 }}>—</span>,
    },
    {
      key: 'inputPrice',
      header: 'Input $/1M',
      className: 'pricing-cell',
      sortable: true,
      sortValue: (p) => p.pricing.input,
      render: (p) => `$${p.pricing.input.toFixed(2)}`,
    },
    {
      key: 'outputPrice',
      header: 'Output $/1M',
      className: 'pricing-cell',
      sortable: true,
      sortValue: (p) => p.pricing.output,
      render: (p) => `$${p.pricing.output.toFixed(2)}`,
    },
    {
      key: 'actions',
      header: '',
      render: (p) => {
        const isBuiltIn = BUILT_IN_PROVIDER_KEYS.includes(p.key);
        if (isBuiltIn) return null;
        return (
          <div className="provider-actions">
            <button
              className="provider-action-btn delete"
              onClick={(e) => { e.stopPropagation(); handleProviderDelete(p.key); }}
              disabled={providerConfigMutation.isPending}
            >
              Delete
            </button>
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [providerConfigMutation.isPending, providerConfigs]);

  // Client-side search + filters over the in-memory config list (it's small).
  // Free-text matches key, display name, model, and family; the family/status
  // dropdowns AND with it. Ordering is DataTable's, via each column's
  // `sortValue` — prices sort numerically that way, not as strings.
  const visibleProviders = useMemo(() => {
    let rows = providerConfigs;
    if (familyFilter) rows = rows.filter(p => p.family === familyFilter);
    if (statusFilter) rows = rows.filter(p => (p.status || 'active') === statusFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(p =>
        p.key.toLowerCase().includes(q) ||
        p.displayName.toLowerCase().includes(q) ||
        p.modelName.toLowerCase().includes(q) ||
        p.family.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [providerConfigs, search, familyFilter, statusFilter]);

  return (
    <div className="settings-section">
      <div className="section-header">
        <div className="section-header-top">
          <h2>LLM Providers</h2>
          {providerSaveStatus === 'saving' && <span className="status-saving">Saving...</span>}
          {providerSaveStatus === 'saved' && <span className="status-saved">Saved</span>}
          {providerSaveStatus === 'error' && <span className="status-error">Save failed</span>}
        </div>
        <p className="section-description">
          Manage LLM providers, model names, pricing, and per-provider LLM defaults. Toggle providers on/off for use in pipeline configurations.
        </p>
      </div>

      <SearchFilterBar
        filters={PROVIDER_FILTERS}
        values={filters}
        onChange={setFilters}
        searchPlaceholder="Search key, name, model, family..."
        hideFetch
        trailingActions={
          <Button variant="primary" size="sm" onClick={openAddModal}>+ Add Provider</Button>
        }
      />

      <DataTable
        columns={columns}
        data={visibleProviders}
        rowKey={(p) => p.key}
        onRowClick={(p) => openEditModal(p)}
        rowClassName={(p) => p.status !== 'deprecated' ? '' : 'dt-row--disabled'}
        loading={providerConfigLoading}
        emptyMessage="No providers configured"
        sort={sort}
        onSortChange={setSort}
        compact
        bordered
      />

      <Modal
        isOpen={modalMode !== null}
        onClose={closeModal}
        title={modalMode === 'add' ? 'Add LLM Provider' : 'Edit LLM Provider'}
        size="md"
      >
        <div className="add-provider-form">
          {/* Row 1: Key + Display Name */}
          <div className="form-row">
            <div className="form-field">
              <label>Key *</label>
              <Input
                placeholder="e.g. claude-opus-4-6"
                value={form.key || ''}
                onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                disabled={modalMode === 'edit'}
              />
              <span className="hint">{modalMode === 'edit' ? 'Key cannot be changed after creation' : 'lowercase, dashes only — used as ID'}</span>
            </div>
            <div className="form-field">
              <label>Display Name *</label>
              <Input
                placeholder="e.g. Claude Opus 4.6"
                value={form.displayName || ''}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              />
            </div>
          </div>

          {/* Row 2: Family + Model Name */}
          <div className="form-row">
            <div className="form-field">
              <label>Family *</label>
              <Select
                value={form.family || 'openai'}
                onChange={(e) => {
                  // Family switch invalidates the model choice — clear it and
                  // re-enter dropdown mode for the new family's model list.
                  setModelNameCustom(false);
                  setForm({ ...form, family: e.target.value as LLMProviderFamily, modelName: '' });
                }}
              >
                {FAMILY_OPTIONS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </Select>
            </div>
            <div className="form-field">
              <label>Model Name *</label>
              {(() => {
                // Dropdown fed by the vendor's list-models API (via the
                // backend, using the configured key). Free-text fallback when
                // the list is unavailable or the model isn't listed yet.
                const models = familyModels?.models ?? [];
                if (modelNameCustom || models.length === 0) {
                  return (
                    <>
                      <Input
                        placeholder="e.g. claude-opus-4-6-20260101"
                        value={form.modelName || ''}
                        onChange={(e) => setForm({ ...form, modelName: e.target.value })}
                      />
                      <span className="hint">
                        exact model ID sent to the provider API
                        {models.length > 0 && (
                          <> · <a onClick={() => setModelNameCustom(false)} style={{ cursor: 'pointer' }}>pick from list</a></>
                        )}
                      </span>
                    </>
                  );
                }
                // Keep a saved model selectable even if the vendor list no
                // longer includes it (renamed/retired) — flagged in the label.
                const options = form.modelName && !models.includes(form.modelName)
                  ? [form.modelName, ...models]
                  : models;
                return (
                  <>
                    <Select
                      value={form.modelName || ''}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') { setModelNameCustom(true); return; }
                        setForm({ ...form, modelName: e.target.value });
                      }}
                    >
                      <option value="">— Select model —</option>
                      {options.map(m => (
                        <option key={m} value={m}>
                          {m}{!models.includes(m) ? ' (not in vendor list)' : ''}
                        </option>
                      ))}
                      <option value="__custom__">Custom (type manually)…</option>
                    </Select>
                    <span className="hint">live list from the {form.family} models API</span>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Row 3: Status + Backup */}
          <div className="form-row">
            <div className="form-field">
              <label>Status</label>
              <Select
                value={form.status || 'active'}
                onChange={(e) => {
                  const newStatus = e.target.value as 'active' | 'deprecated';
                  setForm({ ...form, status: newStatus });
                }}
              >
                <option value="active">Active</option>
                <option value="deprecated">Deprecated</option>
              </Select>
              <span className="hint">Deprecated providers cannot be used at runtime — fromKey() throws and they are hidden from provider pickers.</span>
            </div>
            <div className="form-field">
              <label>Backup Provider <span style={{ fontWeight: 'normal', color: 'var(--text-tertiary)', fontSize: 11 }}>(failover)</span></label>
              {(() => {
                const candidates = providerConfigs.filter(p =>
                  p.key !== form.key &&
                  (p.status || 'active') !== 'deprecated' &&
                  p.family !== 'mock'
                );
                const currentBackup = form.backupProvider;
                const currentBackupProvider = currentBackup
                  ? providerConfigs.find(p => p.key === currentBackup)
                  : undefined;
                const currentInputPrice = form.pricing?.input || 0;
                const backupInputPrice = currentBackupProvider?.pricing?.input || 0;
                const isMoreExpensive = backupInputPrice > 1.5 * currentInputPrice && currentInputPrice > 0;
                const multiplier = currentInputPrice > 0 ? (backupInputPrice / currentInputPrice).toFixed(1) : '?';
                return (
                  <>
                    <Select
                      value={form.backupProvider || ''}
                      onChange={(e) => setForm({ ...form, backupProvider: e.target.value || undefined })}
                    >
                      <option value="">— None —</option>
                      {candidates.map(p => (
                        <option key={p.key} value={p.key}>
                          {p.displayName} ({p.family})
                        </option>
                      ))}
                    </Select>
                    <span className="hint">
                      Failover target when this provider fails. Same-family backups won't survive a provider-wide API outage.
                    </span>
                    {isMoreExpensive && (
                      <div
                        style={{
                          marginTop: 6,
                          padding: '6px 10px',
                          borderRadius: 4,
                          backgroundColor: 'rgba(245, 158, 11, 0.12)',
                          border: '1px solid rgba(245, 158, 11, 0.35)',
                          color: '#fbbf24',
                          fontSize: 11,
                          lineHeight: 1.4,
                        }}
                      >
                        Backup is ~{multiplier}x more expensive on input.
                        Failover events will increase your cost.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* API key env-var override — honored by the google family only.
              Holds the env-var NAME (e.g. GEMINI_API_KEY_PAID); the secret
              itself stays in Azure function app settings. Dropdown is fed by
              /console/providers/api-key-envs (names of keys actually set on
              the function app) so a typo or missing key can't be picked. */}
          {form.family === 'google' && (
            <div className="form-row">
              <div className="form-field">
                <label>API Key Env Var <span style={{ fontWeight: 'normal', color: 'var(--text-tertiary)', fontSize: 11 }}>(optional)</span></label>
                {(() => {
                  const available = apiKeyEnvs?.google ?? [];
                  // Keep a stale saved value selectable so an edit doesn't
                  // silently drop it — flagged as "not set" in the label.
                  const options = form.apiKeyEnv && !available.includes(form.apiKeyEnv)
                    ? [...available, form.apiKeyEnv]
                    : available;
                  return (
                    <Select
                      value={form.apiKeyEnv || ''}
                      onChange={(e) => setForm({ ...form, apiKeyEnv: e.target.value || undefined })}
                    >
                      <option value="">— Family default (GEMINI_API_KEY) —</option>
                      {options.map(name => (
                        <option key={name} value={name}>
                          {name}{!available.includes(name) ? ' (not set on function app!)' : ''}
                        </option>
                      ))}
                    </Select>
                  );
                })()}
                <span className="hint">
                  Function-app setting holding this provider's API key — only keys
                  set on the backend are listed. Lets a paid-key twin of the same
                  model serve as the backup provider.
                </span>
              </div>
            </div>
          )}

          {/* Row 4: Pricing */}
          <div className="form-row">
            <div className="form-field">
              <label>Input price ($/1M tokens)</label>
              <NumberInput
                className="ui-input"
                min={0}
                step="0.01"
                placeholder="0.00"
                value={form.pricing?.input}
                onChange={(v) => setForm({
                  ...form,
                  pricing: { ...(form.pricing || { input: 0, output: 0 }), input: v ?? 0 }
                })}
              />
            </div>
            <div className="form-field">
              <label>Output price ($/1M tokens)</label>
              <NumberInput
                className="ui-input"
                min={0}
                step="0.01"
                placeholder="0.00"
                value={form.pricing?.output}
                onChange={(v) => setForm({
                  ...form,
                  pricing: { ...(form.pricing || { input: 0, output: 0 }), output: v ?? 0 }
                })}
              />
            </div>
          </div>

          {/* ── Adaptive LLM Defaults Section ──────────────────────────── */}
          {form.family !== 'mock' && (
            <>
              <div style={{
                borderTop: '1px solid #374151',
                marginTop: 16,
                paddingTop: 16,
              }}>
                <h4 style={{ margin: '0 0 4px', fontSize: 13, color: 'var(--text-secondary)' }}>
                  LLM Defaults
                  {isReasoning && (
                    <span style={{
                      marginLeft: 8,
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      backgroundColor: 'rgba(99, 102, 241, 0.2)',
                      color: '#818cf8',
                    }}>
                      REASONING MODEL
                    </span>
                  )}
                </h4>
                <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: '0 0 12px' }}>
                  Provider-level defaults. Pipeline step configs override these per-call.
                </p>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px 24px',
                padding: 16,
                backgroundColor: '#1f2937',
                borderRadius: 8,
              }}>
                {/* Temperature */}
                {caps.temperature && (
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, fontWeight: 600 }}>
                      TEMPERATURE
                    </label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="2"
                      placeholder="default"
                      value={form.temperature ?? ''}
                      onChange={(e) => setForm({
                        ...form,
                        temperature: e.target.value === '' ? undefined : parseFloat(e.target.value),
                      })}
                      style={inputStyle}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      0 = deterministic, 1 = default, 2 = max creative
                    </div>
                  </div>
                )}

                {/* Max Tokens */}
                {caps.maxTokens && (
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, fontWeight: 600 }}>
                      MAX TOKENS
                    </label>
                    <Input
                      type="number"
                      step="256"
                      min="256"
                      placeholder="default"
                      value={form.maxTokens ?? ''}
                      onChange={(e) => setForm({
                        ...form,
                        maxTokens: e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                      })}
                      style={inputStyle}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      Max output tokens for the response
                    </div>
                  </div>
                )}

                {/* Reasoning Effort (OpenAI reasoning models) */}
                {caps.reasoningEffort && (
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, fontWeight: 600 }}>
                      REASONING EFFORT
                    </label>
                    <Select
                      value={form.reasoningEffort ?? ''}
                      onChange={(e) => setForm({
                        ...form,
                        reasoningEffort: e.target.value === '' ? undefined : e.target.value as ReasoningEffort,
                      })}
                      style={inputStyle}
                    >
                      <option value="">{form.family === 'deepseek' ? 'Off (no thinking)' : 'Default (high)'}</option>
                      <option value="minimal">Minimal</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </Select>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      {form.family === 'deepseek'
                        ? <>Any value turns thinking on: minimal/low → <code>low</code>, medium/high → <code>high</code></>
                        : <>Maps to <code>reasoning.effort</code> on the Responses API</>}
                    </div>
                  </div>
                )}

                {/* Thinking Budget (Anthropic / Gemini) */}
                {caps.thinkingBudgetTokens && (
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, fontWeight: 600 }}>
                      THINKING BUDGET (TOKENS)
                    </label>
                    <Input
                      type="number"
                      step="1024"
                      min="0"
                      placeholder="default (0 = disabled)"
                      value={form.thinkingBudgetTokens ?? ''}
                      onChange={(e) => setForm({
                        ...form,
                        thinkingBudgetTokens: e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                      })}
                      style={inputStyle}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      {form.family === 'anthropic'
                        ? 'Anthropic thinking budget. Min 1024. Set 0 to disable.'
                        : 'Gemini thinking budget. Set 0 to disable.'}
                    </div>
                  </div>
                )}

                {/* Reasoning Summary Mode (OpenAI reasoning models) */}
                {caps.reasoningSummary && (
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, fontWeight: 600 }}>
                      REASONING SUMMARY
                    </label>
                    <Select
                      value={form.reasoningSummary ?? 'auto'}
                      onChange={(e) => setForm({ ...form, reasoningSummary: e.target.value as ReasoningSummaryMode })}
                      style={inputStyle}
                    >
                      <option value="none">None — no summary</option>
                      <option value="auto">Auto — model decides length</option>
                      <option value="concise">Concise — shorter summaries</option>
                      <option value="detailed">Detailed — full reasoning trace</option>
                    </Select>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      Extra output tokens that surface internal reasoning
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Realtime / voice config — only meaningful for realtime audio
              providers. Shown when the model name says realtime, or when
              either field already has a value (so existing config is never
              silently hidden while it still gets saved). */}
          {((form.modelName ?? '').toLowerCase().includes('realtime') || !!form.voice || !!form.url) && (
            <>
              <div style={{ marginTop: 24, marginBottom: 8 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>
                  Realtime / Voice
                </h4>
                <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: '0 0 12px' }}>
                  Only used by realtime audio providers (e.g. OpenAI Realtime). Ignored for text providers.
                </p>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px 24px',
                padding: 16,
                backgroundColor: '#1f2937',
                borderRadius: 8,
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, fontWeight: 600 }}>
                    DEFAULT VOICE
                  </label>
                  <Select
                    value={form.voice ?? ''}
                    onChange={(e) => setForm({ ...form, voice: e.target.value === '' ? undefined : e.target.value })}
                    style={inputStyle}
                  >
                    <option value="">— none —</option>
                    {OPENAI_REALTIME_VOICES.map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </Select>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    Default voice for realtime sessions (user may override per request)
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, fontWeight: 600 }}>
                    ENDPOINT URL
                  </label>
                  <Input
                    type="text"
                    placeholder="https://api.openai.com/v1/realtime/sessions"
                    value={form.url ?? ''}
                    onChange={(e) => setForm({ ...form, url: e.target.value === '' ? undefined : e.target.value })}
                    style={inputStyle}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    Non-standard API endpoint, used by the voice chat flow
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="add-provider-modal-footer">
            <button className="provider-action-btn cancel" onClick={closeModal}>
              Cancel
            </button>
            <button
              className="provider-action-btn save"
              onClick={handleSave}
              disabled={!form.key || !form.displayName || !form.modelName}
            >
              {modalMode === 'add' ? 'Add Provider' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
